import ApplicationFilters from '@/components/DashboardFilters/ApplicationFilters';
import DashboardLayout from '@/components/layouts/DashboardLayout';
import {
   Table,
   TableBody,
   TableHead,
   TableHeader,
   TableRow,
} from "@/components/ui/table";
import { useMediaQuery } from '@/hooks/useMediaQuery';
import useTabNavigation from '@/hooks/useTabNavigation';
import useTourGuide from '@/hooks/useTourGuide';
import { useTypewriter } from '@/hooks/useTypewriter';
import { gql, useQuery } from '@apollo/client';
import { FileX } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApplications } from '../../../hooks/useApplications';
import PCOApplicationRow from './PCOApplicationRow';

const GET_CERTIFICATES = gql`
   query GetCertificates($status: String) {
      getCertificates(status: $status) {
         id
         certificateNumber
         applicationId
         applicationType
         certificateStatus
         dateCreated
         dateIssued
         expiryDate
         dateApproved
         forestRangerSignature {
            data
            contentType
         }
         chiefTSDSignature {
            data
            contentType
         }
         penrCenrOfficerSignature {
            data
            contentType
         }
         csawCertificateData {
            registrationType
            ownerName
            address
            chainsawDetails {
               brand
               model
               serialNumber
               dateOfAcquisition
               powerOutput
               maxLengthGuidebar
               countryOfOrigin
               purchasePrice
            }
            purpose
            otherDetails
         }
         covCertificateData {
            applicationNumber
            name
            driverName
            driverAddress
            originAddress
            destinationAddress
            forestProducts {
               kind
               species
               scientificName
               numberOfTrees
               quantity {
                  value
                  unit
               }
               volume {
                  cubicMeters
                  boardFeet
               }
            }
            conveyance
            vehiclePlateNumber
            consignee
            permitNumber
         }
         ptprCertificateData {
            ownerName
            address
            contactNumber
            email
            octTctNumber
            lotNumber
            barangay
            municipality
            province
            totalLotArea
            areaDevotedToPlantation
            spacing
            treeSpeciesPlanted
            datePlanted
         }
         pltpCertificateData {
            name
            address
            lotNumber
            octNumber
            treeInventory {
               species
               scientificName
               numberOfTreesInventoried
               grossVolume
               netVolume
               treeStatus
            }
            totalTrees
            totalGrossVolume
            totalNetVolume
         }
         pltcpCertificateData {
            name
            occupation
            projectLocation
            projectAddress
            purpose
            treeInventory {
               species
               scientificName
               numberOfTreesInventoried
               grossVolume
               netVolume
               treeCategory
            }
            totalTrees
            totalGrossVolume
            totalNetVolume
         }
         orderOfPayment {
            id
            OOPstatus
            officialReceipt {
               orNumber
               dateIssued
               amount
               paymentMethod
               remarks
            }
         }
      }
   }
`;

const PENRCENROfficerDashboard = () => {
   const navigate = useNavigate();
   const location = useLocation();

   const [filters, setFilters] = useState({
      searchTerm: '',
      applicationType: '',
      dateRange: {
         from: undefined,
         to: undefined
      }
   });

   const mainTabs = useMemo(() => ['Pending Signature', 'Signed Certificates'], []);

   // Initialize tab navigation
   const {
      activeMainTab,
      handleMainTabChange: handleTabChange
   } = useTabNavigation({
      mainTabs,
      defaultMainTab: 'Pending Signature'
   });

   // Use useMemo for query parameters
   const queryParams = useMemo(() => {
      switch (activeMainTab) {
         case 'Pending Signature':
            return {
               hasCertificate: true,
               currentStage: 'PendingSignatureByPENRCENROfficer',
               $or: [
                  {
                     // For Certificate of Verification
                     $and: [
                        { applicationType: 'Certificate of Verification' },
                        { certificateSignedByChiefTSD: true },
                        { certificateSignedByPENRCENROfficer: false }
                     ]
                  },
                  {
                     // For other application types
                     $and: [
                        { applicationType: { $ne: 'Certificate of Verification' } },
                        { certificateSignedByPENRCENROfficer: false }
                     ]
                  }
               ]
            };
         case 'Signed Certificates':
            return {
               hasCertificate: true,
               certificateSignedByPENRCENROfficer: true,
               currentStage: 'PendingRelease'
            };
         default:
            return {};
      }
   }, [activeMainTab]);

   const { applications, loading: appLoading, error: appError, refetch: refetchApps } = useApplications(queryParams);
   const { data: certificatesData, loading: certificatesLoading, error: certificatesError, refetch: refetchCertificates }
      = useQuery(GET_CERTIFICATES, {
         variables: { status: activeMainTab === 'Pending Signature' ? 'Pending Signature' : 'Complete Signatures' },
         skip: !activeMainTab.includes('Certificates'),
      });

   // Define handleRefetch
   const handleRefetch = useCallback(() => {
      if (activeMainTab.includes('Certificates')) {
         refetchCertificates();
      } else {
         refetchApps();
      }
   }, [activeMainTab, refetchCertificates, refetchApps]);

   useEffect(() => {
      refetchApps();
   }, [refetchApps, activeMainTab]);

   useEffect(() => {
      const pollInterval = setInterval(handleRefetch, 30000);
      return () => clearInterval(pollInterval);
   }, [handleRefetch]);

   const filteredApplications = useMemo(() => {
      if (!applications || !Array.isArray(applications)) {
         return [];
      }

      return applications.filter(app => {
         const matchesSearch = app.applicationNumber.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
            app.applicationType.toLowerCase().includes(filters.searchTerm.toLowerCase());

         const matchesType = !filters.applicationType ||
            filters.applicationType === "all" ||
            app.applicationType === filters.applicationType;

         const matchesDateRange = (() => {
            if (!filters.dateRange.from && !filters.dateRange.to) return true;
            const appDate = new Date(app.dateOfSubmission);
            appDate.setHours(0, 0, 0, 0);
            const fromDate = filters.dateRange.from ? new Date(filters.dateRange.from) : null;
            const toDate = filters.dateRange.to ? new Date(filters.dateRange.to) : null;
            if (fromDate) fromDate.setHours(0, 0, 0, 0);
            if (toDate) toDate.setHours(0, 0, 0, 0);
            return (!fromDate || appDate >= fromDate) && (!toDate || appDate <= toDate);
         })();

         return matchesSearch && matchesType && matchesDateRange;
      });
   }, [applications, filters]);

   const getStatusColor = (status) => {
      switch (status.toLowerCase()) {
         case 'pending review': return 'bg-yellow-100 text-yellow-800';
         case 'approved': return 'bg-green-100 text-green-800';
         default: return 'bg-gray-100 text-gray-800';
      }
   };

   const isMobile = useMediaQuery('(max-width: 640px)');

   const descriptions = useMemo(() => ({
      'Pending Signature': 'This is the list of permits/certificates pending for your signature.',
      'Signed Certificates': 'This is the list of permits/certificates that you have signed.'
   }), []);

   const descriptionText = useTypewriter(descriptions[activeMainTab] || '');

   const renderTabDescription = () => (
      <div className="mb-4 -mt-4">
         <h1 className="text-sm min-h-[20px] text-black dark:text-gray-300">{descriptionText}</h1>
      </div>
   );

   const renderContent = () => {
      if (appLoading) {
         return <div className="flex justify-center py-8">Loading applications...</div>;
      }

      if (appError) {
         return <div className="text-destructive text-center py-8">Error loading applications</div>;
      }

      if (filteredApplications.length === 0) {
         return (
            <div className="text-center py-8">
               <FileX className="mx-auto h-12 w-12 text-muted-foreground" />
               <h3 className="mt-2 text-lg font-semibold">No applications found</h3>
               <p className="text-sm text-muted-foreground">
                  {filters.applicationType ?
                     `No applications found for ${filters.applicationType}` :
                     'No applications available'}
               </p>
            </div>
         );
      }

      if (isMobile) {
         return (
            <div className="space-y-4">
               {filteredApplications.map((app) => (
                  <PCOApplicationRow
                     key={app.id}
                     app={app}
                     onReviewComplete={handleRefetch}
                     getStatusColor={getStatusColor}
                     currentTab={activeMainTab}
                     isMobile={true}
                  />
               ))}
            </div>
         );
      }

      return (
         <Table>
            <TableHeader>
               <TableRow>
                  <TableHead className="w-[25%]">Application Number</TableHead>
                  <TableHead className="w-[20%] text-center">Application Type</TableHead>
                  <TableHead className="w-[17%] text-center">Request Type</TableHead>
                  <TableHead className="w-[20%] text-center">Date</TableHead>
                  <TableHead className="w-[15%] text-center">Status</TableHead>
                  <TableHead className="w-[15%] text-center">Actions</TableHead>
               </TableRow>
            </TableHeader>
            <TableBody>
               {filteredApplications.map((app) => (
                  <PCOApplicationRow
                     key={app.id}
                     app={app}
                     onReviewComplete={handleRefetch}
                     getStatusColor={getStatusColor}
                     currentTab={activeMainTab}
                     isMobile={false}
                  />
               ))}
            </TableBody>
         </Table>
      );
   };

   const { startTour: initiateTour } = useTourGuide({
      tourId: 'penr-cenr-officer-tour',
      onFinish: () => {
         console.log('Tour finished');
      },
   });

   const penrCenrOfficerTourSteps = useMemo(() => {
      // Base steps that are always available
      const baseSteps = [
         {
            target: '.main-tabs',
            content: 'Switch between different application stages using these tabs',
            placement: 'bottom',
            disableBeacon: true,
         },
         {
            target: '.applications-filters',
            content: 'Filter applications by type and date range',
            placement: 'bottom',
         }
      ];

      // Add table steps based on whether we have data or not
      if (filteredApplications?.length > 0) {
         baseSteps.push(
            {
               target: '.applications-table',
               content: 'View all applications that need your attention',
               placement: 'top',
            },
            {
               target: '.table-actions',
               content: 'Here you can find different actions for each application',
               placement: 'left',
            },
            {
               target: '.view-action',
               content: 'Click here to view the complete details of the application',
               placement: 'left',
            },
            {
               target: '.history-action',
               content: 'Click here to view the application history',
               placement: 'left',
            }
         );

         // Add specific steps based on the current tab
         if (activeMainTab === 'Pending Signature') {
            baseSteps.push(
               {
                  target: '.sign-action',
                  content: 'Click here to sign the permit/certificate',
                  placement: 'left',
               },
               {
                  target: '.view-certificate-action',
                  content: 'Click here to view the permit/certificate',
                  placement: 'left',
               }
            );
         } else if (activeMainTab === 'Signed Certificates') {
            baseSteps.push(
               {
                  target: '.view-certificate-action',
                  content: 'Click here to view the signed permit/certificate',
                  placement: 'left',
               },
               {
                  target: '.undo-action',
                  content: 'Click here to undo your signature if needed',
                  placement: 'left',
               }
            );
         }
      } else {
         baseSteps.push({
            target: '.applications-table',
            content: 'Applications will appear here once they need your attention',
            placement: 'top',
         });
      }

      return baseSteps;
   }, [activeMainTab, filteredApplications?.length]);

   // Add useEffect for tour initialization
   useEffect(() => {
      const tourButton = document.querySelector('.help-tour-button');
      if (tourButton) {
         tourButton.disabled = appLoading || certificatesLoading;
      }
   }, [appLoading, certificatesLoading]);

   const handleStartTour = useCallback(async () => {
      // Wait for data to be loaded
      if (appLoading || certificatesLoading) {
         toast.info('Please wait while data is loading...');
         return;
      }

      // Start the tour
      initiateTour(penrCenrOfficerTourSteps);
   }, [appLoading, certificatesLoading, initiateTour, penrCenrOfficerTourSteps]);

   return (
      <DashboardLayout
         title="PENR/CENR Officer Dashboard"
         description="Manage and process applications and certificates"
         onRefresh={handleRefetch}
         isMobile={isMobile}
         mainTabs={mainTabs}
         activeMainTab={activeMainTab}
         onMainTabChange={handleTabChange}
         tabDescription={renderTabDescription()}
         filters={<ApplicationFilters filters={filters} setFilters={setFilters} />}
         helpTour={handleStartTour}
      >
         {renderContent()}
      </DashboardLayout>
   );
};

export default PENRCENROfficerDashboard;
